/* Starting a bulk binding change — the act the Bulk changes tab could only describe.
 *
 * Until this, a run existed only as an API call: the screen listed history and said so honestly.
 * The reference design closes that, and its structure is the argument — an action toggle, criteria,
 * a live match line, a ceiling meter, the candidate rows, and a confirmation that separates what
 * will change from what will be skipped before the one button that starts anything.
 *
 * **Two properties carry it, and both are the platform's rather than the design's.**
 *
 * `willChange` vs `willSkip` — a binding already in the requested state is skipped by the run and
 * left alone by a later revert, so a confirmation promising the selected total over-promises: "412
 * selected" where 374 move. The run's own report makes that split afterwards; making it here is
 * what lets somebody choose differently while they still can.
 *
 * The 5,000 ceiling is shown *before* submission, because the API refuses the whole request over it
 * rather than truncating. A refusal discovered after submitting would be the interface withholding
 * something it already knew — and truncating silently would be worse: a run over the bindings
 * somebody did not choose.
 *
 * **The candidate listing is capped at 500 by the route**, which is below the 5,000 a run may carry.
 * That is stated on the screen rather than hidden: a select-all over a truncated read would promise
 * a selection the page cannot see, which is the truncation-as-completeness defect this codebase
 * records repeatedly.
 */

import { useQueries } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { EDIT_BINDINGS_PERMISSION, can, useIdentity } from '@/entities/session';
import { ruleVersionsQuery, severityTone, useRules } from '@/features/rules';
import { Alert } from '@/shared/ui/Alert';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { CANDIDATE_PAGE, useBindingCandidates, useRequestBulkRun } from '../api/bulk';
import {
  DEFAULT_CHUNK,
  SELECTION_CEILING,
  actionEvent,
  chunkCount,
  matchesFilters,
  meterTone,
  selectionFacts,
  submitBlock,
} from '../model/presentation';
import type { BulkAction, CandidateFilters } from '../model/presentation';
import type { Rule, RuleBinding, Severity } from '@/shared/api/generated/rules';

/** How many distinct rules' version lists this screen will fetch, to name `rule_version_id`.
 *
 * The same bound `DatasetDetail.tsx` uses for the identical join. A picker over up to 500
 * candidate bindings is realistically bound to a handful of rules — this is a ceiling against a
 * pathological tenant, not a number expected to bite in practice; past it a version renders as
 * "current" rather than a fan-out with no limit. */
const MAX_RULE_VERSION_LOOKUPS = 12;
const RULE_VERSION_STALE_MS = 5 * 60 * 1000;

const SEVERITIES: readonly Severity[] = ['blocker', 'error', 'warning', 'info'];

export function BulkStarter(): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  /* **Three states, not two, and the two-state version was a defect this screen shipped for one
     run.** `can` denies while the identity read is in flight and while it has failed, so a boolean
     gate cannot tell "your role does not carry this" from "we have not asked yet" — and the first
     reads as a claim about somebody's permissions made from a request that never answered. The
     notification bell records the same failure: it claimed *nothing is waiting for you* for the
     length of every identity read. Seen live here, on an expired token. */
  const standing: 'unknown' | 'forbidden' | 'permitted' = identity.isPending
    ? 'unknown'
    : identity.isError
      ? 'unknown'
      : can(identity.data, EDIT_BINDINGS_PERMISSION)
        ? 'permitted'
        : 'forbidden';
  const permitted = standing === 'permitted';

  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<BulkAction>('disable');
  const [filters, setFilters] = useState<CandidateFilters>({
    rule: 'all',
    severity: 'all',
    pattern: '',
  });
  const [chosen, setChosen] = useState<readonly string[]>([]);
  const [chunk, setChunk] = useState(String(DEFAULT_CHUNK));

  /* The candidates a run of *this* action could be built from: a disable reads enabled bindings, an
     enable reads disabled ones. Switching the action therefore changes the population, which is
     why it clears the selection — carrying ids across would keep rows that are now all no-ops. */
  const wanted = action === 'enable' ? 'disabled' : 'enabled';
  const candidates = useBindingCandidates(wanted, open && permitted);
  const rules = useRules('', { enabled: open && permitted });
  const request = useRequestBulkRun();

  /* Memoised rather than `?? []` inline: a fresh empty array every render makes both memos below
     recompute on every render, which the lint rule is right to call out — and on a 500-row listing
     that is the filter and the selection join redone for nothing. */
  const all = useMemo(() => candidates.data ?? [], [candidates.data]);
  const matching = useMemo(
    () => all.filter((binding) => matchesFilters(binding, filters)),
    [all, filters],
  );

  /* **A rule's name and kind, not its id.** The list is already fetched for the filter dropdown
     above, so naming a candidate row costs nothing extra — the join is free, it was only never
     made. */
  const rulesById = useMemo(
    () => new Map((rules.data?.items ?? []).map((rule) => [rule.id, rule])),
    [rules.data],
  );

  /* The version *number* needs its own read — `rule_version_id` names nothing outside the versions
     list of the rule it belongs to. Bounded exactly as `DatasetDetail.tsx` bounds the identical
     join, and over distinct rules rather than distinct bindings: up to 500 candidate rows share a
     small number of rules in practice. */
  const distinctRuleIds = useMemo(
    () =>
      [...new Set(all.map((binding) => binding.rule_id))].slice(
        0,
        MAX_RULE_VERSION_LOOKUPS,
      ),
    [all],
  );
  const versionReads = useQueries({
    queries: distinctRuleIds.map((ruleId) => ({
      ...ruleVersionsQuery(ruleId),
      retry: false,
      staleTime: RULE_VERSION_STALE_MS,
    })),
  });
  const versionByVersionId = useMemo(() => {
    const found = new Map<string, string>();
    for (const result of versionReads) {
      for (const version of result.data?.items ?? []) {
        found.set(version.id, version.version);
      }
    }
    return found;
  }, [versionReads]);
  const chosenSet = useMemo(() => new Set(chosen), [chosen]);
  const selected = useMemo(
    () => all.filter((binding) => chosenSet.has(binding.id)),
    [all, chosenSet],
  );

  const facts = selectionFacts(selected, action);
  const tone = meterTone(facts);
  const block = submitBlock(facts, chunk);
  const chunks = chunkCount(facts, chunk);

  const reset = (): void => {
    setChosen([]);
    request.reset();
  };
  const changeFilters = (next: Partial<CandidateFilters>): void => {
    setFilters((current) => ({ ...current, ...next }));
    /* A filter change clears the selection rather than keeping off-screen ids: a submit carrying
       rows the current criteria do not show is a run over bindings nobody is looking at. */
    reset();
  };

  if (standing === 'unknown') {
    /* Nothing, rather than either answer. The starter is one control on a page whose history is
       the point; an absent control for a moment is honest, and a wrong sentence about somebody's
       role is not. */
    return <></>;
  }

  if (standing === 'forbidden') {
    return (
      <div className="lq-band lq-band--neutral">
        <div className="lq-band__title">{t('bulk.start.denied.title')}</div>
        <div className="lq-band__body">{t('bulk.start.denied.body')}</div>
      </div>
    );
  }

  return (
    <>
      {request.isSuccess && (
        <div className="lq-band lq-band--ok" role="status">
          <div className="lq-band__title">{t('bulk.start.accepted.title')}</div>
          <div className="lq-band__body">{t('bulk.start.accepted.body')}</div>
        </div>
      )}

      <div className="lq-bk__starter">
        <div className="lq-bk__starterHead">
          <div>
            <div className="lq-bk__starterTitle">{t('bulk.start.title')}</div>
            <div className="lq-bk__starterBody">{t('bulk.start.body')}</div>
          </div>
          <button
            type="button"
            className={
              open
                ? 'lq-bk__starterToggle lq-bk__starterToggle--open'
                : 'lq-bk__starterToggle'
            }
            aria-expanded={open}
            onClick={() => {
              setOpen(!open);
              reset();
            }}
          >
            {open ? t('bulk.start.close') : t('bulk.start.choose')}
          </button>
        </div>

        {open && (
          <div className="lq-bk__starterBodyPanel">
            <div className="lq-bk__actionRow">
              <span className="lq-bk__actionLabel">{t('bulk.start.change')}</span>
              {(['disable', 'enable'] as readonly BulkAction[]).map((each) => (
                <button
                  key={each}
                  type="button"
                  className={
                    each === action
                      ? 'lq-bk__actionPick lq-bk__actionPick--on'
                      : 'lq-bk__actionPick'
                  }
                  aria-pressed={each === action}
                  onClick={() => {
                    setAction(each);
                    reset();
                  }}
                >
                  {t(`bulk.start.action.${each}`)}
                </button>
              ))}
              <span className="lq-bk__actionNote">
                {t(`bulk.start.actionNote.${action}`)}
              </span>
            </div>

            <div className="lq-bk__criteriaTitle">{t('bulk.start.criteria')}</div>
            <div className="lq-bk__criteria">
              <label className="lq-bk__field">
                <span className="lq-bk__fieldLabel">{t('bulk.start.rule')}</span>
                <select
                  className="lq-bk__input"
                  value={filters.rule}
                  onChange={(event) => {
                    changeFilters({ rule: event.target.value });
                  }}
                >
                  <option value="all">{t('bulk.start.everyRule')}</option>
                  {(rules.data?.items ?? []).map((rule) => (
                    <option key={rule.id} value={rule.id}>
                      {rule.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="lq-bk__field">
                <span className="lq-bk__fieldLabel">{t('bulk.start.severity')}</span>
                <select
                  className="lq-bk__input"
                  value={filters.severity}
                  onChange={(event) => {
                    changeFilters({ severity: event.target.value });
                  }}
                >
                  <option value="all">{t('bulk.start.anySeverity')}</option>
                  {SEVERITIES.map((severity) => (
                    <option key={severity} value={severity}>
                      {t(`bulk.start.severity.${severity}`)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="lq-bk__field">
                <span className="lq-bk__fieldLabel">{t('bulk.start.pattern')}</span>
                <input
                  className="lq-bk__input lq-mono"
                  value={filters.pattern}
                  placeholder="gold.*"
                  onChange={(event) => {
                    changeFilters({ pattern: event.target.value });
                  }}
                />
                <span className="lq-bk__fieldHint">{t('bulk.start.patternHint')}</span>
              </label>

              <div className="lq-bk__field">
                <span className="lq-bk__fieldLabel">{t('bulk.start.state')}</span>
                {/* Not a control: which switch positions are candidates follows from the action,
                    so offering it as a filter would let somebody build a run of pure no-ops and
                    then be told nothing would change. */}
                <div className="lq-bk__stateFixed">
                  {t(`bulk.start.stateFixed.${action}`)}
                </div>
                <span className="lq-bk__fieldHint">
                  {t(`bulk.start.stateNote.${action}`)}
                </span>
              </div>
            </div>

            {candidates.isPending && <SkeletonRows rows={5} columns={6} />}
            {candidates.isError && (
              <Alert tone="error" title={t('bulk.start.candidatesFailed')}>
                {candidates.error.message}
              </Alert>
            )}

            {candidates.isSuccess && (
              <>
                <div className="lq-bk__matchRow">
                  <div className="lq-bk__matchLine">
                    {matching.length === 0
                      ? t('bulk.start.noMatch')
                      : t('bulk.start.matchLine', { count: matching.length })}
                  </div>
                  <button
                    type="button"
                    className="lq-bk__quiet"
                    disabled={matching.length === 0}
                    onClick={() => {
                      setChosen(matching.map((binding) => binding.id));
                      request.reset();
                    }}
                  >
                    {t('bulk.start.selectAll', { count: matching.length })}
                  </button>
                  <button
                    type="button"
                    className="lq-bk__quiet"
                    disabled={chosen.length === 0}
                    onClick={reset}
                  >
                    {t('bulk.start.clear')}
                  </button>
                </div>

                <div className="lq-bk__meterBlock">
                  <div className="lq-bk__meterHead">
                    <span className="lq-bk__meterLabel">{t('bulk.start.selected')}</span>
                    <span className={`lq-bk__meterCount lq-bk__meter--${tone}`}>
                      {facts.selected.toLocaleString()}
                    </span>
                    <span className="lq-bk__meterOf">
                      {t('bulk.start.ofCeiling', { ceiling: SELECTION_CEILING })}
                    </span>
                  </div>
                  <div className="lq-bk__meterTrack">
                    <div
                      className={`lq-bk__meterFill lq-bk__meter--${tone}`}
                      style={{ width: `${String(Math.round(facts.fill * 100))}%` }}
                    />
                  </div>
                  <div className={`lq-bk__meterNote lq-bk__meter--${tone}`}>
                    {facts.overCeiling
                      ? t('bulk.start.meter.over', { excess: facts.excess })
                      : facts.selected === 0
                        ? t('bulk.start.meter.empty')
                        : tone === 'near'
                          ? t('bulk.start.meter.near')
                          : t('bulk.start.meter.normal', { ceiling: SELECTION_CEILING })}
                  </div>
                </div>

                {facts.overCeiling && (
                  <Alert tone="error" title={t('bulk.start.over.title')}>
                    {t('bulk.start.over.body', {
                      selected: facts.selected,
                      ceiling: SELECTION_CEILING,
                    })}
                  </Alert>
                )}

                {matching.length === 0 && all.length > 0 && (
                  <div className="lq-band lq-band--neutral">
                    <div className="lq-band__title">{t('bulk.start.noMatch.title')}</div>
                    <div className="lq-band__body">{t('bulk.start.noMatch.body')}</div>
                  </div>
                )}

                {all.length === 0 && (
                  <div className="lq-band lq-band--neutral">
                    <div className="lq-band__title">
                      {t(`bulk.start.noneAvailable.${action}`)}
                    </div>
                    <div className="lq-band__body">
                      {t(`bulk.start.noneAvailableBody.${action}`)}
                    </div>
                  </div>
                )}

                {facts.selected > 0 && facts.willChange === 0 && (
                  <Alert tone="warning" title={t(`bulk.start.allNoop.${action}`)}>
                    {t('bulk.start.allNoopBody', { count: facts.selected })}
                  </Alert>
                )}

                {matching.length > 0 && (
                  <>
                    <div className="lq-table-scroll lq-bk__candidates">
                      <table className="lq-table lq-table--ledger">
                        <thead>
                          <tr>
                            <th
                              className="lq-bk__pickCol"
                              aria-label={t('bulk.start.column.pick')}
                            />
                            <th>{t('bulk.start.column.rule')}</th>
                            <th>{t('bulk.start.column.severity')}</th>
                            <th>{t('bulk.start.column.selector')}</th>
                            <th>{t('bulk.start.column.now')}</th>
                            <th>{t('bulk.start.column.created')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {matching.map((binding) => (
                            <CandidateRow
                              key={binding.id}
                              binding={binding}
                              rule={rulesById.get(binding.rule_id) ?? null}
                              versionLabel={
                                versionByVersionId.get(binding.rule_version_id) ?? null
                              }
                              action={action}
                              checked={chosenSet.has(binding.id)}
                              onToggle={() => {
                                setChosen((current) =>
                                  current.includes(binding.id)
                                    ? current.filter((id) => id !== binding.id)
                                    : [...current, binding.id],
                                );
                                request.reset();
                              }}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="lq-bk__note">
                      {/* The route caps at 500, which is below the 5,000 a run may carry — said
                          rather than hidden, because a select-all over a truncated read would
                          promise a selection this page cannot see. */}
                      {all.length >= CANDIDATE_PAGE
                        ? t('bulk.start.rowsCapped', { cap: CANDIDATE_PAGE })
                        : t('bulk.start.rowsAll', { count: matching.length })}
                    </p>
                  </>
                )}

                {facts.selected > 0 && (
                  <div className="lq-bk__confirm">
                    <div className="lq-bk__confirmTitle">{t('bulk.start.confirm')}</div>
                    <div className="lq-runstats lq-bk__confirmStats">
                      <div className="lq-runstat">
                        <span className="lq-runstat__label">
                          {t('bulk.start.stat.change')}
                        </span>
                        <span className="lq-bk__confirmWord">
                          {t(`bulk.start.action.${action}`)}
                        </span>
                        <span className="lq-bk__countNote">
                          {t('bulk.start.stat.changeNote')}
                        </span>
                      </div>
                      <div className="lq-runstat">
                        <span className="lq-runstat__label">
                          {t('bulk.start.stat.willChange')}
                        </span>
                        <span
                          className={
                            facts.willChange === 0
                              ? 'lq-runstat__value lq-bk__warnValue'
                              : 'lq-runstat__value'
                          }
                        >
                          {facts.willChange.toLocaleString()}
                        </span>
                        <span className="lq-bk__countNote">
                          {t('bulk.start.stat.willChangeNote')}
                        </span>
                      </div>
                      <div className="lq-runstat">
                        <span className="lq-runstat__label">
                          {t('bulk.start.stat.willSkip')}
                        </span>
                        <span
                          className={
                            facts.willSkip > 0
                              ? 'lq-runstat__value lq-bk__warnValue'
                              : 'lq-runstat__value lq-bk__zero'
                          }
                        >
                          {facts.willSkip.toLocaleString()}
                        </span>
                        <span className="lq-bk__countNote">
                          {t(`bulk.start.stat.willSkipNote.${action}`)}
                        </span>
                      </div>
                      <div className="lq-runstat">
                        <span className="lq-runstat__label">
                          {t('bulk.start.stat.by')}
                        </span>
                        <span className="lq-bk__confirmWho">
                          {identity.data?.subject ?? t('bulk.start.stat.byUnknown')}
                        </span>
                        <span className="lq-bk__countNote">
                          {t('bulk.start.stat.byNote')}
                        </span>
                      </div>
                    </div>

                    <div className="lq-bk__chunkRow">
                      <label className="lq-bk__field lq-bk__chunkField">
                        <span className="lq-bk__fieldLabel">{t('bulk.start.chunk')}</span>
                        <input
                          className="lq-bk__input lq-mono"
                          value={chunk}
                          inputMode="numeric"
                          onChange={(event) => {
                            setChunk(event.target.value);
                          }}
                        />
                      </label>
                      <div className="lq-bk__chunkNote">
                        {chunks === null
                          ? t('bulk.start.chunkBounds')
                          : t('bulk.start.chunkNote', {
                              size: Number(chunk.trim()),
                              chunks,
                            })}
                      </div>
                    </div>

                    <div className="lq-bk__strip">
                      <span>
                        {t('bulk.start.confirmLine', {
                          selected: facts.selected,
                          change: facts.willChange,
                          skip: facts.willSkip,
                        })}
                      </span>
                    </div>

                    {request.isError && (
                      <Alert tone="error" title={t('bulk.start.failed')}>
                        {request.error.message}
                      </Alert>
                    )}

                    <div className="lq-bk__submitRow">
                      <div className="lq-bk__submitHint">
                        {t(`bulk.start.block.${block}`)}
                      </div>
                      <button type="button" className="lq-bk__cancel" onClick={reset}>
                        {t('bulk.start.cancel')}
                      </button>
                      <button
                        type="button"
                        className="lq-bk__submit"
                        disabled={block !== 'none' || request.isPending}
                        onClick={() => {
                          request.mutate(
                            {
                              action: actionEvent(action),
                              bindingIds: chosen,
                              chunkSize: Number(chunk.trim()),
                            },
                            {
                              onSuccess: () => {
                                /* Cleared and closed on success: hard rule 7's key stops a retry
                                   becoming a second run, and clearing stops a deliberate
                                   re-submit without having re-chosen. */
                                setChosen([]);
                                setOpen(false);
                              },
                            },
                          );
                        }}
                      >
                        {t(`bulk.start.submit.${action}`, { count: facts.selected })}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function CandidateRow({
  binding,
  rule,
  versionLabel,
  action,
  checked,
  onToggle,
}: {
  readonly binding: RuleBinding;
  /** `null` while the rules listing has not answered yet, or the rule no longer exists — the
   * second is a real possibility a picker over bindings must survive, since a binding can outlive
   * the rule it names if the rule is later retired. */
  readonly rule: Rule | null;
  /** `null` while unresolved, past the lookup's bound, or the version has been superseded and no
   * longer appears in the rule's own version list. */
  readonly versionLabel: string | null;
  readonly action: BulkAction;
  readonly checked: boolean;
  readonly onToggle: () => void;
}): ReactElement {
  const { t } = useTranslation();
  /* Every candidate would change by construction — the listing is filtered to the opposite state —
     but the cell says so per row anyway, because a filter changed elsewhere or a binding toggled
     while the picker is open makes that assumption false, and a row silently promising a change it
     would skip is the one thing the confirmation exists to prevent. */
  const noop = binding.enabled === (action === 'enable');

  return (
    <tr className="lq-bk__row" onClick={onToggle}>
      <td className="lq-bk__pickCol">
        <input
          type="checkbox"
          className="lq-bk__pick"
          checked={checked}
          aria-label={t('bulk.start.pickRow', { selector: selectorText(binding) })}
          onChange={onToggle}
          onClick={(event) => {
            /* The row's own handler already toggles; without this the click bubbles and toggles
               twice, which reads as the checkbox not working. */
            event.stopPropagation();
          }}
        />
      </td>
      <td>
        {rule === null ? (
          /* The one place an id is still shown, and only because there is genuinely no name to
             show — a binding whose rule was retired after it was bound. Named as a gap rather than
             silently printed as if it meant something. */
          <span className="lq-muted" title={binding.rule_id}>
            {t('bulk.start.ruleGone')}
          </span>
        ) : (
          rule.name
        )}
        <div className="lq-bk__runId">
          {rule === null ? t('bulk.start.kindUnknown') : t(`rules.kind.${rule.kind}`)}
          {versionLabel !== null && ` · v${versionLabel}`}
        </div>
      </td>
      <td>
        {/* The rules feature's own tone map and the shared badge — a second answer to what
            `blocker` looks like is how two screens come to disagree about it. */}
        <StatusBadge
          tone={severityTone(binding.severity)}
          label={t(`bulk.start.severity.${binding.severity}`)}
        />
      </td>
      <td className="lq-bk__selector">{selectorText(binding)}</td>
      <td>
        <span className={binding.enabled ? 'lq-bk__undoOk' : 'lq-bk__undoNo'}>
          {t(binding.enabled ? 'bulk.start.now.enabled' : 'bulk.start.now.disabled')}
        </span>
        <div className="lq-bk__undoNote">
          {t(noop ? 'bulk.start.effect.skip' : 'bulk.start.effect.change')}
        </div>
      </td>
      <td className="lq-bk__who">
        {binding.created_by}
        <div className="lq-bk__undoNote">{binding.created_at.slice(0, 10)}</div>
      </td>
    </tr>
  );
}

/** The binding's selectors as one readable string.
 *
 * Joined rather than showing the first: selectors are a *union*, so a binding with two of them
 * covers both and rendering one would understate what a run touches.
 */
function selectorText(binding: RuleBinding): string {
  const parts = binding.selectors
    .map((selector) => selector.pattern ?? selector.tag ?? selector.role ?? '')
    .filter((text) => text !== '');
  return parts.length === 0 ? '—' : parts.join(', ');
}
